import { useState } from 'react';
import { CoordinatePrecision, formatKilometers, type PrivacyNotice } from '@fleet/shared';
import { PrivacyNoticeModal } from './PrivacyNoticeModal';

/**
 * Kurzfassung dessen, was aus dem Fahrzeug übernommen wird.
 *
 * Zwei Sätze, weil ein Kasten mit zehn Zeilen im Buchungsformular überlesen
 * wird und ein bloßes „Ich habe die Hinweise gelesen" nichts mitteilt. Die
 * Anonymisierungsangabe stammt aus derselben Konfiguration wie der vollständige
 * Text — beide nennen deshalb dieselbe Zahl.
 */
function summarize(notice: PrivacyNotice): string {
  const stored =
    'Gespeichert werden Beginn und Ende der Fahrt, Kilometerstand und Tankstand — nicht der Weg dazwischen.';

  if (notice.anonymization.policy === CoordinatePrecision.REMOVED) {
    return `${stored} Bei Privatfahrten werden Start- und Endpunkt gar nicht erst gespeichert.`;
  }

  const radius = notice.anonymization.uncertaintyRadiusMeters;
  return `${stored} Bei Privatfahrten werden Start- und Endpunkt vergröbert: Der gespeicherte Ort kann bis zu rund ${formatKilometers(radius!)} vom tatsächlichen abweichen.`;
}

/**
 * Bestätigung der Datenschutzhinweise im Buchungsformular.
 *
 * Verlangt wird sie bei **jeder** Buchung von allen, die die betreffende
 * Fahrzeuggruppe nicht selbst freigeben dürfen. Eine einmalige Kenntnisnahme
 * beim ersten Anmelden wäre bequemer und wertloser: Der Hinweis, dass bei einer
 * Privatfahrt die Standorte vergröbert werden, nützt genau in dem Moment, in
 * dem jemand die Fahrtart wählt.
 *
 * Wer freigeben darf, sieht denselben Text als Verweis, aber ohne Häkchen — die
 * Bestätigung wäre dort eine Selbstauskunft derer, die die Regeln pflegen.
 */
export function PrivacyConsent({
  notice,
  error,
  required,
  accepted,
  onChange,
}: {
  notice?: PrivacyNotice;
  /** Der Hinweis war nicht zu laden — ohne ihn kann niemand bestätigen. */
  error?: unknown;
  required: boolean;
  accepted: boolean;
  onChange: (accepted: boolean) => void;
}) {
  const [showing, setShowing] = useState(false);

  const link = (
    <button
      type="button"
      onClick={() => setShowing(true)}
      className="cursor-pointer underline underline-offset-2 hover:no-underline"
    >
      Datenschutzhinweise
    </button>
  );

  /**
   * Für Manager und Fuhrparkleitung: dieselbe Aussage, aber ohne Häkchen.
   *
   * Trotzdem als ganze Fläche und nicht als Textlink — vorher stand hier eine
   * unterstrichene Zeile in Grau auf 12 Pixel, und die übersieht man
   * zuverlässig. Bewusst in Weiß und nicht in Bernstein: Bernstein heißt in
   * dieser Oberfläche „hier fehlt noch etwas", und hier fehlt nichts.
   */
  if (!required) {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowing(true)}
          className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3 text-left transition-colors hover:border-slate-400 hover:bg-slate-50"
        >
          <span>
            <span className="block text-sm font-semibold text-slate-900">
              Datenschutzhinweise
            </span>
            <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">
              {notice ? summarize(notice) : 'Was bei einer Buchung gespeichert wird.'}
            </span>
          </span>
          <span aria-hidden className="shrink-0 text-lg leading-none text-slate-400">
            ›
          </span>
        </button>

        {showing && <PrivacyNoticeModal onClose={() => setShowing(false)} />}
      </>
    );
  }

  /**
   * Ohne Text keine Bestätigung — und deshalb auch keine Buchung.
   *
   * Das ist die unbequeme, aber richtige Reaktion: Ein Häkchen anzubieten, das
   * sich auf einen nicht angezeigten Hinweis bezieht, wäre eine Bestätigung
   * ohne Gegenstand.
   */
  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-900">
        <p>Die Datenschutzhinweise sind gerade nicht abrufbar.</p>
        <p className="mt-1 text-xs opacity-80">
          Ohne sie lässt sich nicht buchen. Nach dem Neuladen der Seite sollte es wieder
          gehen; bleibt es dabei, ist die API nicht erreichbar.
        </p>
      </div>
    );
  }

  return (
    <>
      {/*
        Der Kasten fällt auf, solange er etwas will, und beruhigt sich danach.
        Ein dauerhaft gelber Block im Formular wird nach der dritten Buchung
        genauso überlesen wie ein grauer — die Aufmerksamkeit gehört dem
        offenen Punkt, nicht dem erledigten.
      */}
      <div
        className={[
          'rounded-lg border-2 px-4 py-4 transition-colors',
          accepted ? 'border-slate-200 bg-slate-50' : 'border-amber-400 bg-amber-50',
        ].join(' ')}
      >
        <p className="text-sm font-semibold text-slate-900">Bevor du buchst</p>

        <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
          {notice ? summarize(notice) : 'Datenschutzhinweise werden geladen …'}
        </p>

        {/*
          Das Kästchen sitzt auf eigenem weißen Grund: Es ist das einzige
          Element hier, das angefasst werden will, und soll nicht wie ein
          weiterer Satz des Hinweises aussehen.
        */}
        <label
          className={[
            'mt-3 flex cursor-pointer items-center gap-3 rounded-md border bg-white px-3 py-2.5',
            accepted ? 'border-slate-200' : 'border-amber-300',
          ].join(' ')}
        >
          <input
            type="checkbox"
            checked={accepted}
            disabled={!notice}
            onChange={(event) => onChange(event.target.checked)}
            className="size-5 shrink-0 cursor-pointer accent-slate-900"
          />
          <span className="text-sm font-medium">Ich habe gelesen, was gespeichert wird.</span>
        </label>

        <p className="mt-2.5 text-xs text-slate-500">
          {link}
          {notice && (
            <>
              {' · Fassung '}
              <span className="font-mono">{notice.version}</span>
            </>
          )}
        </p>
      </div>

      {showing && <PrivacyNoticeModal onClose={() => setShowing(false)} />}
    </>
  );
}
